import { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  MenuItem,
  Grid,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Button } from "@mui/material";
import api from '../config/api';
import useStore from "../config/store";
import { useTranslation } from 'react-i18next';

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  maxHeight: "80vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 0,
  p: 4,
  border: "none",
  outline: "none",
};

const UserOrganizationModal = ({ open, onClose, user, allOrganizations }) => {
  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState({});
  const [loading, setLoading] = useState(false);

  const { authToken, showAlert } = useStore();
  const { t } = useTranslation();

  useEffect(() => {
    if (user) {
      setSelectedOrgs(user.organizations || []);
      const initialProjects = {};
      allOrganizations.forEach((org) => {
        if (user.organizations?.includes(org.uid)) {
          const matchedProjects = org.projects
            ?.filter((p) => user.projects?.includes(p.uid))
            .map((p) => p.uid);
          initialProjects[org.uid] = matchedProjects || [];
        }
      });
      setSelectedProjects(initialProjects);
    }
  }, [user, allOrganizations]);

  const handleOrgChange = (event) => {
    const incoming = event.target.value;
    const isSingleOrgRole = ["user", "admin"].includes(user?.rol);

    const orgUids = isSingleOrgRole
      ? (incoming.length ? [incoming[incoming.length - 1]] : [])
      : incoming;

    setSelectedOrgs(orgUids);

    setSelectedProjects((prev) => {
      if (isSingleOrgRole) {
        const onlyOrg = orgUids[0];
        if (!onlyOrg) return {};

        const org = allOrganizations.find((o) => o.uid === onlyOrg);
        const matchedProjects =
          org?.projects
            ?.filter((p) => user?.projects?.includes(p.uid))
            .map((p) => p.uid) || [];

        return { [onlyOrg]: prev[onlyOrg] ?? matchedProjects };
      }

      const updated = { ...prev };

      orgUids.forEach((uid) => {
        if (updated[uid] === undefined) {
          const org = allOrganizations.find((o) => o.uid === uid);
          const matchedProjects =
            org?.projects
              ?.filter((p) => user?.projects?.includes(p.uid))
              .map((p) => p.uid) || [];
          updated[uid] = matchedProjects;
        }
      });

      Object.keys(updated).forEach((uid) => {
        if (!orgUids.includes(uid)) delete updated[uid];
      });

      return updated;
    });
  };

  const handleProjectChange = (orgUid, value) => {
    setSelectedProjects((prev) => ({
      ...prev,
      [orgUid]: value,
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await api.put(
        `/organizations/assignOrganizations/${user.uid}`,
        { organizations: selectedOrgs },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      const allProjects = Array.from(
        new Set(
          selectedOrgs.flatMap(
            (orgId) => selectedProjects[orgId] || []
          )
        )
      );

      await api.put(
        `/projects/assignProjectsDirectly/${user.uid}`,
        { projects: allProjects },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      showAlert(t("users.assignOrganizationProjectsSuccess"), "success");
      onClose();
    } catch (err) {
      console.error(err);
      showAlert(t("users.assignOrganizationProjectsError"), "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Typography variant="h6" gutterBottom sx={{ textAlign: "center" }}>
          {t('users.userOrganizationEdit')} {user?.name}
        </Typography>

        <Box sx={{ mt: 3 }}>
          <TextField
            label={t('organizations.title')}
            fullWidth
            select
            SelectProps={{ multiple: true }}
            value={selectedOrgs}
            onChange={handleOrgChange}
            sx={{ mb: 3 }}
          >
            {allOrganizations.map((org) => (
              <MenuItem
                key={org.uid}
                value={org.uid}
                sx={{
                  "&.Mui-selected": {
                    backgroundColor: "#e0e0e0",
                    fontWeight: 600,
                  },
                  "&.Mui-selected:hover": {
                    backgroundColor: "#d5d5d5",
                  },
                }}
              >
                {org.name}
              </MenuItem>
            ))}
          </TextField>

          {selectedOrgs.map((orgUid) => {
            const org = allOrganizations.find((o) => o.uid === orgUid);
            if (!org) return null;

            return (
              <Box key={orgUid} sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t('users.projectsOf')} {org.name}
                </Typography>
                <TextField
                  label={t('projects.title')}
                  fullWidth
                  select
                  SelectProps={{ multiple: true }}
                  value={selectedProjects[orgUid] || []}
                  onChange={(e) => handleProjectChange(orgUid, e.target.value)}
                >
                  {(org.projects || []).map((project) => (
                    <MenuItem
                      key={project.uid}
                      value={project.uid}
                      sx={{
                        "&.Mui-selected": {
                          backgroundColor: "#e0e0e0",
                          fontWeight: 600,
                        },
                        "&.Mui-selected:hover": {
                          backgroundColor: "#d5d5d5",
                        },
                      }}
                    >
                      {project.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            );
          })}
        </Box>

        <Grid container spacing={2} justifyContent="center" sx={{ mt: 4 }}>
          <Grid item>
            <Button variant="outlined" onClick={onClose} disabled={loading}>
              {t('cancel')}
            </Button>
          </Grid>
          <Grid item>
            <LoadingButton
              variant="contained"
              onClick={handleSave}
              loading={loading}
            >
              {t('save')}
            </LoadingButton>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};

export default UserOrganizationModal;
